import { asText } from "@prismicio/client"
import { useMemo } from "react"

import { useTranslation } from "@bounce/i18n"

import { useFAQs as useLuggageStorageFAQs } from "../../../../components/LuggageStorage/FAQs/useFAQs"
import type { CityData } from "@/libs/bounce/cities"
import type { BoilerplateJSON, FAQ, SafeguardFaq } from "@/utils/boilerplate"
import { buildFaqs } from "@/utils/boilerplate"
import { getPreFaq } from "@/utils/faqs"

export type CityInterpolations = {
  city: string
  price: string
  url: string
}

const safeguardCity: SafeguardFaq<CityInterpolations> = arg => arg

export type UseFAQsOptions = {
  boilerplateContent?: BoilerplateJSON | null
  city: Pick<CityData, "slug" | "translatedName">
  interpolations: CityInterpolations
  prismicFAQs?: CityData["faq"] | null
}

export const useFAQs = ({ interpolations, boilerplateContent, prismicFAQs, city }: UseFAQsOptions): FAQ[] => {
  const { t } = useTranslation("marketing-ls-city")
  const luggageStorageFaqs = useLuggageStorageFAQs({ place: city })

  return useMemo(() => {
    const prismicFAQContent =
      prismicFAQs
        ?.map(({ question, answer }) => ({
          id: asText(question),
          question: asText(question),
          answer: asText(answer),
        }))
        .filter(({ question, answer }) => !!question && !!answer) ?? []

    const getLuggageStorageFaq = (faqId: string) => getPreFaq<CityInterpolations>(faqId, luggageStorageFaqs)

    const faqsStrings = [
      safeguardCity({
        id: "How much does luggage storage cost in {{city}}?",
        key: "howMuchDoesLuggageStorageCostIn",
        question: int =>
          t(
            "cmp.cityFaqs.howMuchDoesLuggageStorageCostIn.question",
            "How much does luggage storage cost in {{city}}?",
            int,
          ),
        answer: int =>
          t(
            "cmp.cityFaqs.howMuchDoesLuggageStorageCostIn.answer",
            "We charge from {{price}} per day (24 hours) for luggage storage in {{city}}.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.cityFaqs.howMuchDoesLuggageStorageCostIn.structuredAnswer",
            "<p>We charge from {{price}} per day (24 hours) for luggage storage in {{city}}. <a href='{{url}}'>Click here</a> to see luggage storage locations</p>",
            int,
          ),
        requires: [],
      }),
      safeguardCity({
        id: "Does {{city}} have luggage lockers?",
        key: "areThereStorageLockersIn",
        question: int =>
          t("cmp.cityFaqs.areThereStorageLockersIn.question", "Does {{city}} have luggage lockers?", int),
        answer: int =>
          t(
            "cmp.cityFaqs.areThereStorageLockersIn.answer",
            "Yes, but they're not always the best option. Luggage lockers can fill up quickly and may be unavailable when you need them. Some luggage lockers may be too small to fit large or bulky items. Unlike luggage lockers, we don't charge per hour and instead charge a flat rate for 24 hours of storage. We also have partners who watch over your stuff to make sure it's safe, and can answer any questions you may have or give you advice on what to do in {{city}}.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.cityFaqs.areThereStorageLockersIn.structuredAnswer",
            "<p>Yes, but they're not always the best option. Luggage lockers can fill up quickly and may be unavailable when you need them. Some luggage lockers may be too small to fit large or bulky items. Unlike luggage lockers, we don't charge per hour and instead charge a flat rate for 24 hours of storage. We also have partners who watch over your stuff to make sure it's safe, and can answer any questions you may have or give you advice on what to do in {{city}}. <a href='{{url}}'>Click here</a> to see them</p>",
            int,
          ),
        requires: [],
      }),
      safeguardCity(getLuggageStorageFaq("How does Bounce luggage storage work?")),
      safeguardCity({
        id: "Does {{city}} have luggage storage?",
        key: "doesCityHaveLuggageStorage",
        question: int =>
          t(
            "cmp.luggageStorageFaqs.doesCityHaveLuggageStorageOptimized.question",
            "Does {{city}} have luggage storage?",
            int,
          ),
        answer: int =>
          t(
            "cmp.luggageStorageFaqs.doesCityHaveLuggageStorageOptimized.answer",
            "Yes! To find a convenient luggage storage option in {{city}}, use the Bounce app or website to tell us how many bags you have and when you need storage. We’ll show you all of your options so you can enjoy your day in {{city}} without your bags.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.luggageStorageFaqs.doesCityHaveLuggageStorageOptimized.structuredAnswer2",
            "<p>Yes! To find a convenient luggage storage option in {city}, use the Bounce app or website to tell us how many bags you have and when you need storage. We’ll show you all of your options so you can enjoy your day in {city} without your bags.</p>",
            int,
          ),
        requires: [],
      }),
      safeguardCity(getLuggageStorageFaq("What can I store with Bounce?")),
      safeguardCity({
        id: "Is there hourly luggage storage in {{city}}?",
        key: "hourlyStorage",
        question: int => t("cmp.cityFaqs.hourlyStorage.question", "Is there hourly luggage storage in {{city}}?", int),
        answer: int =>
          t(
            "cmp.cityFaqs.hourlyStorage.answer",
            "Yes, but many companies charge per hour and costs can add up fast. We charge one price for 24 hours of storage, so storing your stuff is more affordable in {{city}}.",
            int,
          ),
        structuredAnswer: int =>
          t(
            "cmp.cityFaqs.hourlyStorage.structuredAnswer",
            "<p>Yes, but many companies charge per hour and costs can add up fast. We charge one price for 24 hours of storage, so storing your stuff is more affordable in {{city}}. <a href='{{url}}'>Click here</a> to see them</p>",
            int,
          ),
        requires: [],
      }),
      safeguardCity(getLuggageStorageFaq("How long can I leave my luggage with Bounce?")),
      safeguardCity({
        id: "How do I change or cancel my booking in {{city}}?",
        key: "howDoIChangeOrCancelIn",
        question: int =>
          t("cmp.cityFaqs.howDoIChangeOrCancelIn.question", "How do I change or cancel my booking in {{city}}?", int),
        answer: int =>
          t(
            "cmp.cityFaqs.howDoIChangeOrCancelIn.answer",
            "Plans change, we get it! If you need to change or cancel your booking in {{city}} for any reason, the best way to do it is from your booking details page in the Bounce app. You can edit the dates, drop-off and pick-up times, and number of bags. You can also cancel your booking or rebook at a nearby store. If you can't find your booking details, check the email address you used to make your booking. You can change your booking by following the link there, or reach out to us if you need support.",
            int,
          ),
        requires: [],
      }),
    ]

    const faqsContent = buildFaqs({ faqs: faqsStrings, data: boilerplateContent ?? {}, interpolations, t })

    return [...prismicFAQContent, ...faqsContent]
  }, [prismicFAQs, boilerplateContent, interpolations, t, luggageStorageFaqs])
}
